// hooks/useSlideGenerator.js - PROMPT-ONLY, NO VIDEO EXPORT
import { useState, useRef, useCallback } from 'react';
import { renderSlideToCanvas, selectBestTemplate, downloadSlideAsImage, getTemplatesList } from '../canvas/SlideCanvasRenderer';
import { fetchImagesForSlides } from '../canvas/imageFetcher';
import { useAuth } from '../AuthContext';
import { usePlanTracker } from './usePlanTracker';

export const useSlideGenerator = () => {
  const { user } = useAuth();
  const planTracker = usePlanTracker(user?.uid);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedSlides, setProcessedSlides] = useState([]);
  const [processingError, setProcessingError] = useState(null);
  const [moderationBlock, setModerationBlock] = useState(null);
  const [slideTemplates, setSlideTemplates] = useState({});
  const [slideImages, setSlideImages] = useState([]);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [imageFetchError, setImageFetchError] = useState(null);

  const canvasRef = useRef(null);

  const SERVER_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
  const API_BASE_URL = `${SERVER_BASE_URL}/api`;

  const getFirstValidTemplate = () => {
    const templates = getTemplatesList();
    if (templates.length === 0) return null;
    return templates[0].id;
  };

  const validateTemplateId = (templateId) => {
    if (!templateId) return getFirstValidTemplate();
    const templates = getTemplatesList();
    const exists = templates.some(t => t.id === templateId);
    if (!exists) return getFirstValidTemplate();
    return templateId;
  };

  const fetchSlideImages = async (slides, topic = '') => {
    setIsFetchingImages(true);
    setImageFetchError(null);
    try {
      const images = await fetchImagesForSlides(slides, topic);
      setSlideImages(images);
      return images;
    } catch (error) {
      setImageFetchError(error.message || 'Failed to fetch images');
      return [];
    } finally {
      setIsFetchingImages(false);
    }
  };

  const generateFromPrompt = async (promptText) => {
    setIsProcessing(true);
    setProcessingError(null);
    setModerationBlock(null);

    try {
      const response = await fetch(`${API_BASE_URL}/generate-from-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: promptText,
          userId: user?.uid || 'anonymous'
        })
      });

      const data = await response.json();

      if (data.blocked) {
        setModerationBlock({
          reason: data.reason,
          category: data.category,
          suggestion: data.suggestion,
          type: 'prompt'
        });
        setIsProcessing(false);
        return null;
      }

      if (!response.ok) {
        if (data.limitReached) {
          throw new Error(data.error || 'Monthly limit reached');
        }
        throw new Error(data.error || 'Failed to generate slides from prompt');
      }

      // Pick ONE visual style for the whole deck (based on the first slide's
      // content) instead of letting each slide pick its own style independently.
      // Previously selectBestTemplate() ran per-slide, so slide 1 could end up
      // on a different template family than slide 2 right out of generation.
      // Layout (image left/right/none) still alternates per slide as before.
      const firstSlideContent = data.slides[0]?.extractedContent || {};
      const suggestedTemplate = selectBestTemplate(firstSlideContent);
      const validTemplate = validateTemplateId(suggestedTemplate);
      const baseName = validTemplate.replace(/_(left|right|none)$/, '');

      const templatesMap = {};
      data.slides.forEach((slide, index) => {
        const layoutSuffix = index % 2 === 0 ? '_right' : '_left';
        const candidateId = `${baseName}${layoutSuffix}`;
        templatesMap[index] = validateTemplateId(candidateId);
      });

      setSlideTemplates(templatesMap);
      setProcessedSlides(data.slides);
      await fetchSlideImages(data.slides, promptText);

      // The server already incremented usage for this generation
      // (see checkLimits.js / promptRoutes.js). Pull the fresh count
      // rather than incrementing again locally — avoids double-counting.
      planTracker.refresh();

      return data;

    } catch (err) {
      setProcessingError(err.message || 'Failed to generate slides from prompt');
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const getSlideImageUrl = useCallback((slideIndex) => {
    const imageData = slideImages[slideIndex];
    if (imageData && imageData.success && imageData.image && imageData.image.url) {
      return imageData.image.url;
    }
    return null;
  }, [slideImages]);

  const resetProcessingStates = () => {
    setProcessedSlides([]);
    setProcessingError(null);
    setModerationBlock(null);
    setSlideTemplates({});
    setSlideImages([]);
    setIsFetchingImages(false);
    setImageFetchError(null);
  };

  const handleReset = () => {
    resetProcessingStates();
  };

  const changeSlideTemplate = (slideIndex, templateId) => {
    const validTemplate = validateTemplateId(templateId);
    setSlideTemplates(prev => ({
      ...prev,
      [slideIndex]: validTemplate
    }));
  };

  const downloadSlide = async (slideData, slideIndex) => {
    const canvas = document.createElement('canvas');
    const templateId = validateTemplateId(slideTemplates[slideIndex]);
    const imageUrl = getSlideImageUrl(slideIndex);
    await renderSlideToCanvas(canvas, slideData.extractedContent, templateId, imageUrl);
    downloadSlideAsImage(canvas, slideIndex);
  };

  const copySlideText = async (slideData) => {
    const content = slideData.extractedContent;
    const text = `
${content?.heading || ''}

${content?.subheading || ''}

${content?.bulletPoints?.join('\n') || ''}

${content?.bodyText || ''}
    `.trim();
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // silent fail
    }
  };

  return {
    canvasRef,
    isProcessing,
    processedSlides,
    processingError,
    moderationBlock,
    slideTemplates,
    slideImages,
    isFetchingImages,
    imageFetchError,
    getSlideImageUrl,
    generateFromPrompt,
    fetchSlideImages,
    handleReset,
    changeSlideTemplate,
    downloadSlide,
    copySlideText,
    API_BASE_URL,
    SERVER_BASE_URL,
    setProcessedSlides,
    setSlideTemplates,
    setSlideImages,
    planTracker,
  };
};